import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetChargePointsPaginatedQuery } from '@app/core/service/cpms-bo-charge-point.service';

import { LocationChargePointListUpdateController } from '../location-charge-point-list-update/location-charge-point-list-update.controller';
import {
  LocationChargePointListContextProvider,
  useLocationChargePointListContext,
  useLocationContext,
} from '../location.context';
import * as css from './location-charge-point-list.module.scss';
import { LocationChargePointListView } from './location-charge-point-list.view';

export const LocationChargePointListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getPaginatedQueryParameters } = useLocationChargePointListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetChargePointsPaginatedQuery(
    getPaginatedQueryParameters,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleActionRead = (id: string) => {
    navigate(generatePath(APP_PATH.CHARGE_POINT.DETAILS, { id }));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const {
    Component: LocationChargePointListViewProtected,
    status: resourceStatus,
  } = withProtectedResource(LocationChargePointListView, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.READ,
      }),
    transformDataToResource: transformPaginatedResponse,
  })({
    onActionUpdate: () => setActionUpdateIsDialogOpen(true),
    onActionRead: handleActionRead,
  });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack justifyContent="end" gap={GapEnum.M}>
          <div />
        </Stack>
      )}
      {LocationChargePointListViewProtected}
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Dialog
          onClose={() => setActionUpdateIsDialogOpen(false)}
          open={actionUpdateIsDialogOpen}
          size={DialogSize.LARGE}
          height="100%">
          <Dialog.Header
            onClose={() => setActionUpdateIsDialogOpen(false)}
            title={t('$.location-charge-point-list-update.title')}
          />
          <Dialog.Content>
            <LocationChargePointListUpdateController
              onCancel={() => setActionUpdateIsDialogOpen(false)}
              onSuccess={() => setActionUpdateIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </Stack>
  );
};

export const LocationChargePointListControllerWithContext =
  (): React.JSX.Element => {
    const location = useLocationContext();

    return (
      <LocationChargePointListContextProvider data={{ location }}>
        <LocationChargePointListController />
      </LocationChargePointListContextProvider>
    );
  };
